import {
  labelOf,
  packNotes,
  ROASTS,
  type RoastKey,
  type WeightKey,
  wrapPackName,
} from '@c214/shared';
import { cn, formatRoastDate } from '../../lib/utils';

interface MiniPackProps {
  variant: 'mini';
  name: string;
  className?: string;
}

interface FullPackProps {
  variant?: 'full';
  name: string;
  packSubtitle: string;
  roast: RoastKey;
  notes: string;
  roastedAt: string;
  weight: WeightKey;
  className?: string;
}

const WEIGHT_ON_PACK: Record<WeightKey, string> = {
  '250': '250 g',
  '500': '500 g',
  '1000': '1 kg',
};

// Шов зверху пакета — вертикальні штрихи одним path (без масиву елементів).
const SEAL_PATH = Array.from({ length: 24 }, (_, i) => `M${36 + i * 10} 20V36`).join('');

// Пачка, намальована з даних товару замість PNG із «впеченим» текстом. SVG з
// фіксованим viewBox 3:4 — масштабується разом з елементом; розмір задає className.
// mini — для місць, де пачка ≤ 90px заввишки: лише пакет, «214» і назва.
export function PackArt(props: MiniPackProps | FullPackProps) {
  const mini = props.variant === 'mini';
  const { lines, shrink } = wrapPackName(props.name);
  const nameSize = mini ? (shrink ? 20 : 24) : shrink ? 21 : 26;
  const nameY = mini ? 170 : 150;
  const nameStep = mini ? 30 : 30;

  return (
    <svg
      viewBox="0 0 300 400"
      role="img"
      aria-label={props.name}
      className={cn('block', props.className)}
    >
      <rect x="20" y="14" width="260" height="372" className="fill-surface stroke-line" />
      <rect x="20" y="14" width="260" height="26" className="fill-bg" />
      <path d={SEAL_PATH} className="stroke-line" strokeWidth="1" />
      <rect x="44" y="62" width="44" height="44" className="fill-accent" />
      <text
        x="66"
        y="90"
        textAnchor="middle"
        fontSize="17"
        fontWeight="800"
        className="fill-bg font-sans"
      >
        214
      </text>
      {lines[0] && (
        <text x="44" y={nameY} fontSize={nameSize} fontWeight="800" className="fill-ink font-sans">
          {lines[0]}
        </text>
      )}
      {lines[1] && (
        <text
          x="44"
          y={nameY + nameStep}
          fontSize={nameSize}
          fontWeight="800"
          className="fill-ink font-sans"
        >
          {lines[1]}
        </text>
      )}
      {props.variant !== 'mini' && <PackDetails {...props} />}
      <rect x="20" y="360" width="260" height="26" className="fill-surface-2" />
    </svg>
  );
}

function PackDetails({ packSubtitle, roast, notes, roastedAt, weight }: FullPackProps) {
  const noteLines = packNotes(notes);
  return (
    <>
      <path d="M44 212H256" className="stroke-line" strokeWidth="1" />
      {packSubtitle && (
        <text x="44" y="236" fontSize="10" letterSpacing="1" className="fill-muted font-mono">
          {packSubtitle.toUpperCase()}
        </text>
      )}
      <text x="44" y="254" fontSize="10" letterSpacing="1" className="fill-muted font-mono">
        ОБСМАЖЕННЯ: {labelOf(ROASTS, roast).toUpperCase()}
      </text>
      {noteLines.map((line, index) => (
        <text
          key={line}
          x="44"
          y={276 + index * 15}
          fontSize="10"
          letterSpacing="0.5"
          className="fill-accent font-mono"
        >
          {line}
        </text>
      ))}
      <circle cx="250" cy="318" r="13" className="fill-line" />
      <circle cx="250" cy="318" r="4" className="fill-bg" />
      <text x="44" y="324" fontSize="13" fontWeight="800" className="fill-ink font-sans">
        ОБСМАЖЕНО {formatRoastDate(roastedAt)}
      </text>
      <text x="44" y="344" fontSize="10" letterSpacing="1" className="fill-muted font-mono">
        {WEIGHT_ON_PACK[weight]} / NET
      </text>
    </>
  );
}
